import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import enNav from "./locales/en/nav.json";
import enHome from "./locales/en/home.json";
import enFlow from "./locales/en/flow.json";
import enWidgets from "./locales/en/widgets.json";
import enPages from "./locales/en/pages.json";
import enGamedata from "./locales/en/gamedata.json";
import enKeeper from "./locales/en/keeper.json";
import enPulp from "./locales/en/pulp.json";
import jaCommon from "./locales/ja/common.json";
import jaNav from "./locales/ja/nav.json";
import jaHome from "./locales/ja/home.json";
import jaFlow from "./locales/ja/flow.json";
import jaWidgets from "./locales/ja/widgets.json";
import jaPages from "./locales/ja/pages.json";
import jaGamedata from "./locales/ja/gamedata.json";
import jaKeeper from "./locales/ja/keeper.json";
import jaPulp from "./locales/ja/pulp.json";
import deCommon from "./locales/de/common.json";
import deNav from "./locales/de/nav.json";
import deHome from "./locales/de/home.json";
import deFlow from "./locales/de/flow.json";
import deWidgets from "./locales/de/widgets.json";
import dePages from "./locales/de/pages.json";
import deGamedata from "./locales/de/gamedata.json";
import deKeeper from "./locales/de/keeper.json";
import dePulp from "./locales/de/pulp.json";

export const SUPPORTED_LANGUAGES = ["en", "ja", "de"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const JP_FONT_LINK_ID = "ii-jp-fonts";

function ensureJapaneseFonts() {
  if (typeof document === "undefined") return;
  if (document.getElementById(JP_FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = JP_FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;600;700&family=Noto+Sans+JP:wght@400;500;700&display=swap";
  document.head.appendChild(link);
}

function syncHtmlLang(lng: string) {
  if (typeof document === "undefined") return;
  const normalized = lng.startsWith("ja") ? "ja" : lng.startsWith("de") ? "de" : "en";
  document.documentElement.lang = normalized;
  if (normalized === "ja") ensureJapaneseFonts();
}

// Instantiate the detector so we can register the custom `japanLocale` lookup
// BEFORE i18n.init runs. Registering after init causes a first-paint flash
// where the UI renders in English and then swaps to Japanese on re-render.
const detector = new LanguageDetector();
detector.addDetector({
  name: "japanLocale",
  lookup: () => {
    if (typeof navigator === "undefined") return undefined;
    const langs = (navigator.languages || [navigator.language || ""]).filter(Boolean);
    if (langs.some((l) => l.toLowerCase().startsWith("ja"))) return "ja";
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (tz === "Asia/Tokyo") return "ja";
    } catch {
      /* ignore */
    }
    return undefined;
  },
  // The detector signature also accepts a cacheUserLanguage no-op
  cacheUserLanguage: () => undefined,
} as unknown as Parameters<typeof detector.addDetector>[0]);

detector.addDetector({
  name: "germanLocale",
  lookup: () => {
    if (typeof navigator === "undefined") return undefined;
    const langs = (navigator.languages || [navigator.language || ""]).filter(Boolean);
    if (langs.some((l) => l.toLowerCase().startsWith("de"))) return "de";
    return undefined;
  },
  cacheUserLanguage: () => undefined,
} as unknown as Parameters<typeof detector.addDetector>[0]);

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon, nav: enNav, home: enHome, flow: enFlow, widgets: enWidgets, pages: enPages, gamedata: enGamedata, keeper: enKeeper, pulp: enPulp },
      ja: { common: jaCommon, nav: jaNav, home: jaHome, flow: jaFlow, widgets: jaWidgets, pages: jaPages, gamedata: jaGamedata, keeper: jaKeeper, pulp: jaPulp },
      de: { common: deCommon, nav: deNav, home: deHome, flow: deFlow, widgets: deWidgets, pages: dePages, gamedata: deGamedata, keeper: deKeeper, pulp: dePulp },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    defaultNS: "common",
    ns: ["common", "nav", "home", "flow", "widgets", "pages", "gamedata", "keeper", "pulp"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "japanLocale", "germanLocale", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "ii_lang",
    },
    react: { useSuspense: false },
  });

syncHtmlLang(i18n.language || "en");
i18n.on("languageChanged", syncHtmlLang);

export default i18n;
